import { useCallback, useEffect, useState } from "react";
import { EffectRow, listEffects } from "../api";

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === "ready"
      ? "bg-lemonade text-black"
      : status === "error"
      ? "bg-red-600 text-ivory"
      : status === "processing"
      ? "bg-blue-500 text-ivory"
      : "bg-stone text-charcoal";
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}
    >
      {status}
    </span>
  );
}

// Lightweight per-type summary so the history list stays readable as more
// effect types ship. Unknown shapes fall back to a JSON dump.
function ParamsSummary({ row }: { row: EffectRow }) {
  const p = row.params as Record<string, unknown>;
  if (row.effectType === "overlay-pip") {
    return (
      <div className="text-xs text-concrete">
        {String(p.preset ?? "")} · début {String(p.startTime ?? "")}s · durée{" "}
        {String(p.duration ?? "")}s
      </div>
    );
  }
  if (typeof row.effectType === "string" && row.effectType.startsWith("popart-")) {
    const style = (p.styleParams ?? {}) as Record<string, string>;
    const variants = [style.palette, style.typo, style.energy, style.shape]
      .filter(Boolean)
      .join(" · ");
    return (
      <div className="text-xs text-concrete">
        source: {String(p.sourceMediaKey ?? "—")}
        {variants && <> · {variants}</>}
      </div>
    );
  }
  return null;
}

/**
 * Single effect row with the video kept out of the DOM until the user clicks
 * "Voir la vidéo". Mounting <video> lazily prevents two problems:
 *
 * 1. UX: the listing stays compact — the user scans rows, opens the one they
 *    want, instead of having a wall of autoplay-poster videos.
 *
 * 2. Reload loop bug: the parent polls listEffects() every 4s while any row
 *    is non-terminal. Each call returns a freshly-presigned resultUrl with a
 *    new signature query string. <video src={resultUrl}> sees src change and
 *    remounts mid-playback, which the user perceived as the player "se
 *    rechargeant en boucle". By snapshotting the URL the first time the user
 *    opens the panel and keeping it stable until they close it, the element
 *    stays mounted with a fixed src.
 */
function EffectRowItem({ row }: { row: EffectRow }) {
  const [open, setOpen] = useState(false);
  // Once the user opens the panel, freeze the result URL so subsequent polls
  // don't swap the signed URL underneath the <video>.
  const [frozenUrl, setFrozenUrl] = useState<string | null>(null);

  const ready = row.status === "ready" && row.resultUrl;

  return (
    <li className="rounded-lg border border-stone bg-white p-4">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-charcoal">
            {row.effectType}
          </div>
          <div className="font-mono text-xs text-concrete">
            {row.effectId.slice(0, 16)}…
          </div>
          <div className="text-xs text-concrete">
            {new Date(row.createdAt).toLocaleString("fr-FR")}
          </div>
        </div>
        <StatusBadge status={row.status} />
      </div>

      <div className="mb-2">
        <ParamsSummary row={row} />
      </div>

      {ready && (
        <>
          {!open && (
            <button
              type="button"
              onClick={() => {
                setFrozenUrl(row.resultUrl ?? null);
                setOpen(true);
              }}
              className="flex w-full items-center justify-between rounded-md border border-stone bg-stone/30 px-3 py-2 text-sm text-charcoal transition hover:bg-stone/60"
            >
              <span className="flex items-center gap-2">
                <span className="text-lg leading-none">▶</span>
                <span>Voir la vidéo</span>
              </span>
              <span className="font-mono text-[10px] text-concrete">
                {row.resultKey?.replace(/^effects\//, "") ?? ""}
              </span>
            </button>
          )}

          {open && frozenUrl && (
            <div className="space-y-2">
              <video
                src={frozenUrl}
                controls
                playsInline
                preload="metadata"
                className="w-full max-w-xs rounded-md bg-black"
              />
              <div className="flex gap-3 text-sm">
                <a
                  href={frozenUrl}
                  download
                  className="text-charcoal underline hover:text-black"
                >
                  Télécharger
                </a>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-concrete hover:text-charcoal"
                >
                  Replier
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {row.status === "error" && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
          {row.errorMessage || "Erreur inconnue"}
        </div>
      )}
    </li>
  );
}

export default function AdminEffectsHistory() {
  const [effects, setEffects] = useState<EffectRow[]>([]);
  const [listError, setListError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const rows = await listEffects();
      setEffects(rows);
      setListError(null);
    } catch (e) {
      setListError(e instanceof Error ? e.message : "Liste indisponible");
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Poll every 4s while at least one effect is non-terminal.
  useEffect(() => {
    const pending = effects.some(
      (e) => e.status === "queued" || e.status === "processing"
    );
    if (!pending) return;
    const t = setInterval(() => void reload(), 4000);
    return () => clearInterval(t);
  }, [effects, reload]);

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-bold text-charcoal">Effets effectués</h2>
        <button
          type="button"
          onClick={() => void reload()}
          className="text-xs text-concrete hover:text-charcoal"
        >
          Rafraîchir
        </button>
      </div>

      {listError && (
        <div className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {listError}
        </div>
      )}

      {effects.length === 0 && !listError && (
        <p className="text-sm text-concrete">
          Aucun effet pour le moment. Compose le premier depuis l&apos;onglet
          Effets.
        </p>
      )}

      <ul className="space-y-3">
        {effects.map((e) => (
          <EffectRowItem key={e.effectId} row={e} />
        ))}
      </ul>
    </section>
  );
}
